import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import MondayDate from '@/components/data/date/MondayDate';
import { allocationsCss } from '@digital_monorepo/styling';
import AllocationsView from './AllocationsView';

const Allocations = ({ allocations }) => {
  const { t } = useTranslation();
  const [selectedLevels, setSelectedLevels] = useState(['all']);
  const [projectFilter, setProjectFilter] = useState('');
  const [showChargeability, setShowChargeability] = useState(false);
  const [includeSubs, setIncludeSubs] = useState(true);

  const filterOptions = {
    all: 'All',
    trainee: 'Ts',
    analyst: 'As',
    consultant: 'Cs',
    senior_consultant: 'SCs',
    manager: 'Ms',
    senior_manager: 'SMs',
    associate_partner: 'APs',
    partner: 'Ps'
  };

  const handleFilterChange = value => {
    if (value === 'all') {
      setSelectedLevels(['all']);
      return;
    }

    let newLevels = [...selectedLevels];
    if (newLevels.includes('all')) {
      newLevels = [];
    }

    if (newLevels.includes(value)) {
      newLevels = newLevels.filter(l => l !== value);
    } else {
      newLevels.push(value);
    }

    if (newLevels.length === 0) {
      newLevels = ['all'];
    }

    setSelectedLevels(newLevels);
  };

  const handleClearFilters = () => {
    setSelectedLevels(['all']);
    setProjectFilter('');
  };

  const filteredAllocations = useMemo(() => {
    if (!allocations) return [];
    let base = allocations;
    if (!includeSubs) {
      base = base.filter(a => (a.area || '').toLowerCase() !== 'contractor');
    }
    if (selectedLevels.includes('all')) return base;

    return base.filter(allocation => {
      const level = Number(allocation.FunctionLevel);
      if (isNaN(level)) return false;

      return selectedLevels.some(filter => {
        if (filter === 'trainee') return level < 300000;
        if (filter === 'analyst') return level >= 300000 && level < 400000;
        if (filter === 'consultant') return level >= 400000 && level < 500000;
        if (filter === 'senior_consultant') return level >= 500000 && level < 600000;
        if (filter === 'manager') return level >= 600000 && level < 700000;
        if (filter === 'senior_manager') return level >= 700000 && level < 800000;
        if (filter === 'associate_partner') return level >= 800000 && level < 900000;
        if (filter === 'partner') return level >= 900000;
        return false;
      });
    });
  }, [allocations, selectedLevels, includeSubs]);

  // Generate unique dates for current week + 1 year
  const uniqueDates = useMemo(() => {
    if (!filteredAllocations || filteredAllocations.length === 0) return [];

    // Get current week start (Monday)
    const today = new Date();
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startOfWeek.setDate(today.getDate() - daysToMonday);
    startOfWeek.setHours(0, 0, 0, 0);

    // Get end date (52 weeks from start of current week)
    const endDate = new Date(startOfWeek);
    endDate.setDate(endDate.getDate() + 52 * 7);

    const dates = Array.from(new Set(filteredAllocations.map(entry => entry.StartDate)))
      .filter(dateString => {
        const date = new Date(dateString);
        return date >= startOfWeek && date <= endDate;
      })
      .sort();

    return dates.map(date => MondayDate.formatToUIDate(date));
  }, [filteredAllocations]);

  // Group data by Professional ID with clients per week
  const tableData = useMemo(() => {
    const groupedData = {};

    filteredAllocations.forEach(entry => {
      const profId = entry.ProfessionalID;

      if (!groupedData[profId]) {
        groupedData[profId] = {
          ProfessionalID: entry.ProfessionalID,
          ProfessionalName: entry.ProfessionalName,
          FunctionName: entry.FunctionName,
          FunctionLevel: entry.FunctionLevel,
          Office: entry.Office,
          area: entry.area,
          Skills: entry.Skills,
          ProfessionalRC: entry.ProfessionalRC,
          ClientsByWeek: {} // { date: { clientCode: { name, hours } } }
        };
      }

      // Marker rows for contractors with no real allocations carry a null ClientCode.
      if (!entry.ClientCode) return;

      const formattedDate = MondayDate.formatToUIDate(entry.StartDate);
      if (uniqueDates.includes(formattedDate)) {
        if (!groupedData[profId].ClientsByWeek[formattedDate]) {
          groupedData[profId].ClientsByWeek[formattedDate] = {};
        }

        const clientCode = entry.ClientCode;
        if (!groupedData[profId].ClientsByWeek[formattedDate][clientCode]) {
          groupedData[profId].ClientsByWeek[formattedDate][clientCode] = {
            name: entry.ClientName,
            hours: 0,
            isDraft: false
          };
        }

        groupedData[profId].ClientsByWeek[formattedDate][clientCode].hours += Number(entry.Hours || 0);
        if (entry.Status === 'Draft') {
          groupedData[profId].ClientsByWeek[formattedDate][clientCode].isDraft = true;
        }
      }
    });

    let result = Object.values(groupedData);

    if (projectFilter) {
      const lowerFilter = projectFilter.toLowerCase();
      result = result.filter(prof => Object.values(prof.ClientsByWeek).some(week => Object.values(week).some(client => client.name && client.name.toLowerCase().includes(lowerFilter))));
    }

    return result;
  }, [filteredAllocations, uniqueDates, projectFilter]);

  // Helper function to parse DD/MM/YYYY format
  const parseUIDate = dateString => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Helper function to format date as "DD-MMM"
  const formatWeekHeader = dateString => {
    const date = parseUIDate(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    const month = monthNames[date.getMonth()];

    return `${day}-${month}`;
  };

  // Helper to calculate displayed client data
  const getClientDataForWeek = (clientsByWeek, date) => {
    const clientsInWeek = clientsByWeek?.[date] || {};
    const clientCodes = Object.keys(clientsInWeek);

    if (clientCodes.length === 0) {
      return { name: '0', code: null, totalHours: 0, isDraft: false, chargeability: 0 };
    }

    let maxHours = 0;
    let topClient = '0';
    let topClientCode = null;
    let totalHours = 0;
    let isTopClientDraft = false;
    let nonChargeableHours = 0;
    const nonChargeableKeywords = ['holiday', 'national holiday', 'pto'];

    clientCodes.forEach(clientCode => {
      const client = clientsInWeek[clientCode];
      const hours = client.hours;
      totalHours += hours;

      const clientNameLower = (client.name || '').toLowerCase();
      if (nonChargeableKeywords.some(keyword => clientNameLower.includes(keyword))) {
        nonChargeableHours += hours;
      }

      if (hours > maxHours) {
        maxHours = hours;
        topClient = client.name;
        topClientCode = clientCode;
        isTopClientDraft = client.isDraft;
      }
    });

    const numerator = totalHours - nonChargeableHours;
    const denominator = 40 - nonChargeableHours;
    let chargeability = 0;
    if (denominator > 0) {
      chargeability = (numerator / denominator) * 100;
    } else {
      // If 40h holiday, denominator 0. If user worked 0h chargeability?
      // If denominator is 0, it means FULL week holiday/PTO.
      // If numerator > 0 (worked extra), chargeability > 100?
      // Logic: if denominator <= 0, assume 100% compliant if numerator >= 0?
      // Or simply 100 if fully off?
      // For now, let's treat denominator 0 as 100% chargeability (fully utilized for what was available)
      chargeability = 100;
    }

    return { name: topClient, code: topClientCode, totalHours, isDraft: isTopClientDraft, chargeability };
  };

  // Define columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t('ALLOCATIONS_TABLE.HEADERS.PROFESSIONAL_ID'),
        accessor: 'ProfessionalID',
        filter: 'text',
        sticky: 'left',
        width: 100,
        Cell: ({ row }) => (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '10ch', padding: '0 4px' }} title={row.original.ProfessionalID}>
            {row.original.ProfessionalID}
          </div>
        )
      },
      {
        Header: t('ALLOCATIONS_TABLE.HEADERS.PROFESSIONAL_NAME'),
        accessor: 'ProfessionalName',
        filter: 'text',
        sticky: 'left',
        width: 170,
        Cell: ({ row }) => (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '25ch', padding: '0 4px' }} title={row.original.ProfessionalName}>
            <Link to={`/professionals/${row.original.ProfessionalID}`} target='_blank' rel='noopener noreferrer' className={allocationsCss.linkCell}>
              {row.original.ProfessionalName}
            </Link>
          </div>
        )
      },
      {
        Header: t('ALLOCATIONS_TABLE.HEADERS.FUNCTION_NAME'),
        accessor: 'FunctionName',
        filter: 'text',
        width: 140,
        // Sort by seniority, not by the label. FunctionLevel is a numeric code
        // where higher is more senior (900701 Equity Partner ... 300101 Analyst).
        // sortDescFirst puts the most senior first on the first click; rows with
        // no usable level sort as least senior so they land at the bottom there.
        sortDescFirst: true,
        sortType: (rowA, rowB) => {
          const rank = row => {
            const n = Number(row.original.FunctionLevel);
            return isNaN(n) ? -Infinity : n;
          };
          return rank(rowA) - rank(rowB);
        },
        Cell: ({ value }) => (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '25ch', padding: '0 4px' }} title={value}>
            {value}
          </div>
        )
      },
      {
        Header: t('ALLOCATIONS_TABLE.HEADERS.OFFICE'),
        accessor: 'Office',
        filter: 'text',
        width: 80,
        Cell: ({ value }) => (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '18ch', padding: '0 4px' }} title={value}>
            {value}
          </div>
        )
      },
      {
        Header: t('ALLOCATIONS_TABLE.HEADERS.AREA'),
        accessor: 'area',
        filter: 'text',
        width: 46,
        Cell: ({ value }) => (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '5ch', padding: '0 4px' }} title={value}>
            {value}
          </div>
        )
      },
      {
        Header: t('ALLOCATIONS_TABLE.HEADERS.SKILLS'),
        accessor: 'Skills',
        filter: 'text',
        width: 100,
        Cell: ({ value }) => (
          <div
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '15ch',
              padding: '0 4px'
            }}
            title={value}>
            {value}
          </div>
        )
      }
    ];

    // Add date columns with formatted headers
    const dateColumns = uniqueDates.map((date, index) => ({
      Header: formatWeekHeader(date),
      id: `ClientsByWeek.${date}`,
      accessor: row => {
        const { name, chargeability } = getClientDataForWeek(row.ClientsByWeek, date);
        if (showChargeability) {
          return chargeability;
        }
        return name;
      },
      originalDate: date,
      width: 62,
      Cell: ({ row, value }) => {
        const { name, code, totalHours, isDraft, chargeability } = getClientDataForWeek(row.original.ClientsByWeek, date);

        if (name === '0') {
          return (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f7e1dd',
                width: '100%',
                height: '100%',
                whiteSpace: 'nowrap'
              }}>
              0
            </div>
          );
        }

        let backgroundColor = '#f7e1dd'; // Default red
        let displayText = value;

        if (showChargeability) {
          displayText = `${Math.round(chargeability)}%`;
          if (chargeability >= 70) {
            backgroundColor = '#dfeeda'; // Green
          } else if (chargeability >= 30) {
            backgroundColor = '#f6edce'; // Yellow
          } else {
            backgroundColor = '#f7e1dd'; // Red
          }
        } else {
          // Determine background color based on total hours
          if (name === 'On Leave') {
            backgroundColor = '#e8ece9'; // Gray for On Leave
          } else if (isDraft && totalHours > 0) {
            backgroundColor = '#e9e0f3'; // Light purple for Draft status
          } else if (totalHours >= 40) {
            backgroundColor = '#dfeeda'; // Green for 40h or more
          } else if (totalHours > 8) {
            backgroundColor = '#f6edce'; // Yellow for > 8 and < 40
          }
        }

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: backgroundColor,
              width: '100%',
              height: '100%',
              whiteSpace: 'nowrap'
            }}>
            {!showChargeability ? (
              <Link to={`/clients/${code}`} className={allocationsCss.linkCell} style={{ color: '#333', fontWeight: '500' }}>
                {displayText}
              </Link>
            ) : (
              <span className={allocationsCss.linkCell} style={{ color: '#333', fontWeight: '500' }}>
                {displayText}
              </span>
            )}
          </div>
        );
      }
    }));

    return [...baseColumns, ...dateColumns];
  }, [t, uniqueDates, showChargeability]);

  return (
    <AllocationsView
      columns={columns}
      data={tableData}
      filterOptions={filterOptions}
      selectedLevels={selectedLevels}
      onLevelChange={handleFilterChange}
      projectFilter={projectFilter}
      onProjectFilterChange={setProjectFilter}
      uniqueDates={uniqueDates}
      formatWeekHeader={formatWeekHeader}
      getClientDataForWeek={getClientDataForWeek}
      showChargeability={showChargeability}
      onShowChargeabilityChange={setShowChargeability}
      includeSubs={includeSubs}
      onIncludeSubsChange={setIncludeSubs}
      onClearFilters={handleClearFilters}
    />
  );
};

export default Allocations;
